import {
  HeartPulse, ShieldCheck, Brain, BarChart3, FileCheck, Clock,
  Users, Award, Cpu, Building2, ArrowRight, Check, AlertTriangle,
  Layers, Zap, Receipt, CreditCard, ClipboardCheck, Database,
  RefreshCw, BellRing, Plug, Server, ChevronDown, ChevronUp,
  Send, Loader2, Globe
} from "lucide-react";

/* =========================================================
   CareOps 365 — Patient Billing & Accounting Sub-page Data
   ========================================================= */

export const PB_HERO = {
  eyebrow: "CareOps 365 — Patient Accounting & Billing",
  headline: "Purpose-Built Financial Intelligence for Long-Term Care",
  subheadline:
    "When care gets complex, finance gets complicated. CareOps 365 automates billing, subsidy sequencing, and MOH-compliant invoicing — so your team can focus on what matters.",
  primaryCTA: { label: "Book a CareOps 365 Demo", href: "#pb-contact" },
  secondaryCTA: { label: "See Features", href: "#pb-features" },
};

export const PB_STATS = [
  { value: "38+", label: "Years in Business", icon: Clock },
  { value: "26+", label: "Nursing Homes Served", icon: Building2 },
  { value: "180+", label: "Projects Deployed", icon: Layers },
  { value: "24", label: "Years Microsoft ERP Experience", icon: ShieldCheck },
];

export const PB_PROBLEM = {
  headline: "The Hidden Complexity Behind Every Patient Bill",
  description:
    "Singapore's Long-Term Care landscape is evolving rapidly. By 2025, nearly 1 in 4 Singaporeans will be over 65. Behind every patient lies a matrix of financial variables that must be calculated with precision.",
  factors: [
    "RAF categories & scoring",
    "Means testing tiers",
    "Government subsidies & grants",
    "Service packages & entitlements",
    "Consumables & ad-hoc charges",
    "GST treatments across periods",
    "Government claims submission",
    "Guarantor billing structures",
  ],
  conclusion:
    "Managing this manually is a spreadsheet nightmare. One miscalculation can mean billing disputes, compliance issues, and revenue leakage.",
};

export const PB_WHY_TRUST = {
  headline: "Why Long-Term Care Providers Trust Asterisk",
  subheadline: "We are not new to healthcare finance. We have evolved alongside it.",
  description:
    "Since 2010, our patient accounting platform has journeyed from pilot to scale across Singapore's LTC transformation. CareOps 365 is published on Microsoft AppSource and trusted by finance teams across 26+ nursing homes.",
  credentials: [
    { icon: Award, text: "38 years in business" },
    { icon: ShieldCheck, text: "Microsoft Solutions Partner (Business Applications)" },
    { icon: Globe, text: "Microsoft ISV Partner — CareOps 365 on AppSource" },
    { icon: Layers, text: "180+ projects across 15 industries" },
    { icon: HeartPulse, text: "16+ non-profit organisations supported" },
    { icon: Building2, text: "26+ nursing homes served in Singapore" },
  ],
};

export const PB_BILLING_ENGINE = {
  headline: "Built for Real LTC Billing Complexity",
  subheadline: "Dynamic Billing Engine",
  description:
    "Patients' RAF scores and Means Test categories change over time. Subsidies shift. Sponsorships vary. Charges fluctuate monthly. CareOps 365 automates the entire calculation chain.",
  capabilities: [
    { icon: RefreshCw, text: "Multi-period billing calculations" },
    { icon: Layers, text: "Subsidy and grant prioritisation sequencing" },
    { icon: Users, text: "Sponsor and government matrix allocation" },
    { icon: Receipt, text: "Flexible GST treatments" },
    { icon: Clock, text: "Complex billing cycle handling" },
    { icon: FileCheck, text: "Pro-forma invoice generation" },
    { icon: Send, text: "Automated claims submission" },
  ],
  outcome: "No more manual recalculation. No more billing disputes due to human error.",
};

export const PB_BRIDGE = {
  headline: "A Bridge Between Clinical and Finance",
  description:
    "Most LTC providers juggle clinical systems, government reporting platforms, accounting software, and billing processes separately. CareOps 365 acts as the bridge layer.",
  functions: [
    "Captures patient financial records from clinical systems",
    "Matches Means Test profiles automatically",
    "Manages service packages and entitlements",
    "Tracks subsidies and grants in real time",
    "Integrates into ERP or operates as a full accounting system",
  ],
  outcome: "Your clinical team focuses on care. Your finance team gains clarity.",
};

export const PB_COMPLIANCE = {
  headline: "Designed for Compliance. Built for Confidence.",
  description:
    "As a licensed LTC facility, compliance is non-negotiable. CareOps 365 generates MOH-compliant billing documentation that builds trust — with patients, sponsors, regulators, and auditors.",
  outputs: [
    { icon: FileCheck, text: "Branded invoices and billing statements" },
    { icon: ShieldCheck, text: "MOH-compliant formats" },
    { icon: ClipboardCheck, text: "Transparent charge breakdowns" },
    { icon: Database, text: "Detailed audit-ready billing records" },
  ],
};

export const PB_CASHFLOW = {
  headline: "Accelerate Cash Flow. Reduce Bad Debts.",
  description:
    "In a sector where margins are tight, cash flow discipline matters. Better billing accuracy means faster payments — and faster payments mean stronger financial sustainability.",
  benefits: [
    "Automate scheduled billing",
    "Submit claims digitally in required formats",
    "Reduce reconciliation delays",
    "Improve collection cycles",
  ],
};

export const PB_FEATURES = [
  { icon: Database, title: "Patient Financial Record Management" },
  { icon: CreditCard, title: "Grant and Subsidy Management" },
  { icon: Cpu, title: "RAF & Means Test Matrix Calculation Engine" },
  { icon: Send, title: "Government & ILTC Submission" },
  { icon: Receipt, title: "Multi-period GST Calculation" },
  { icon: Zap, title: "Claims Submission Automation" },
  { icon: Clock, title: "Complex Billing Cycle Handling" },
  { icon: BellRing, title: "Means Test Review Alerts" },
  { icon: Plug, title: "Integration with Clinical Systems" },
  { icon: Server, title: "Full ERP Capability or ERP Integration" },
];

export const PB_CUSTOMERS = [
  { name: "Dover Park Hospice", logo: "/assets/customers/dover-park-hospice.png" },
  { name: "St Lukes Eldercare", logo: "/assets/customers/st-lukes-eldercare.png" },
  { name: "Home Nursing Foundation", logo: "/assets/customers/home-nursing-foundation.jpg" },
  { name: "United Medicare Centre", logo: "/assets/customers/united-medicare-centre.png" },
  { name: "NTUC Health", logo: "/assets/customers/ntuc-health.jpg" },
  { name: "Lentor Health", logo: "/assets/customers/lentor-health.png" },
  { name: "APEX Harmony Lodge", logo: "/assets/customers/apex-harmony-lodge.jpg" },
  { name: "Man Fut Tong Nursing Home", logo: "/assets/customers/man-fut-tong.jpg" },
  { name: "HCA Hospice Care", logo: "/assets/customers/hca-hospice-care.jpg" },
];

export const PB_AUDIENCE = [
  "Nursing Homes",
  "ILTC Facilities",
  "Home Care Providers",
  "Skilled Nursing Facilities",
  "Retirement Villages",
];

export const PB_FAQ = [
  {
    q: "What is CareOps 365?",
    a: "CareOps 365 is Asterisk's purpose-built Patient Accounting & Billing platform developed specifically for Singapore's Long-Term Care ecosystem. It automates complex billing calculations, subsidy sequencing, MOH-compliant invoicing, and claims submission.",
  },
  {
    q: "Who is CareOps 365 designed for?",
    a: "CareOps 365 is designed for nursing homes, ILTC facilities, home care providers, skilled nursing facilities, and retirement villages — particularly those operating in Singapore's regulated healthcare environment.",
  },
  {
    q: "Does CareOps 365 handle MOH-compliant billing?",
    a: "Yes. CareOps 365 generates branded invoices and billing statements in MOH-compliant formats with transparent charge breakdowns and audit-ready records.",
  },
  {
    q: "Can CareOps 365 integrate with our existing clinical or ERP system?",
    a: "Absolutely. CareOps 365 acts as a bridge between clinical systems and finance. It can integrate with your existing ERP or operate as a standalone full accounting system.",
  },
  {
    q: "How does CareOps 365 manage subsidies and grants?",
    a: "CareOps 365 automates subsidy and grant prioritisation sequencing, matches Means Test profiles automatically, and manages the full sponsor and government matrix allocation.",
  },
  {
    q: "Is Asterisk experienced in the Long-Term Care sector?",
    a: "Yes. Asterisk has 38+ years in business, 24 years of Microsoft ERP experience, and has served 26+ nursing homes in Singapore. CareOps 365 is published on Microsoft AppSource.",
  },
];
